"use client";
import { useRef, useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Scene from "@/shared/components/Scene";
import { useClientContext } from "@/shared/components/Context";

import s from "./styles.module.css";

gsap.registerPlugin(ScrollTrigger);

interface TransformState {
  rotation_x: number;
  rotation_y: number;
  rotation_z: number;
  scale_x: number;
  scale_y: number;
  scale_z: number;
  position_x: number;
  position_y: number;
  position_z: number;
}
const BEGIN_DESKTOP: TransformState = {
  rotation_x: 166.81,
  rotation_y: 9.77,
  rotation_z: 146.42,
  scale_x: 2.8,
  scale_y: 2.8,
  scale_z: 2.8,
  position_x: 1850,
  position_y: 2200,
  position_z: 2000,
};

const END_DESKTOP: TransformState = {
  rotation_x: 8.46,
  rotation_y: -14.46,
  rotation_z: -59.07,
  scale_x: 2.8,
  scale_y: 2.8,
  scale_z: 2.8,
  position_x: 1850,
  position_y: 2200,
  position_z: 2000,
};

const BEGIN_MOBILE: TransformState = {
  rotation_x: 158.83,
  rotation_y: 16.77,
  rotation_z: -11.03,
  scale_x: 1.6,
  scale_y: 1.6,
  scale_z: 1.6,
  position_x: 195.98,
  position_y: 1634.05,
  position_z: -2772.29,
};

const END_MOBILE: TransformState = {
  rotation_x: -20,
  rotation_y: -11.35,
  rotation_z: -61.03,
  scale_x: 1.6,
  scale_y: 1.6,
  scale_z: 1.6,
  position_x: 195.98,
  position_y: 1634.05,
  position_z: -2772.29,
};

function interpolate(
  initial: TransformState,
  end: TransformState,
  progress: number
): TransformState {
  const result: Partial<TransformState> = {};

  for (const key in initial) {
    if (initial.hasOwnProperty(key)) {
      const typedKey = key as keyof TransformState;
      const startVal = initial[typedKey];
      const endVal = end[typedKey];
      result[typedKey] = startVal + (endVal - startVal) * (progress / 100);
    }
  }

  return result as TransformState;
}

enum ViewportType {
  Mobile = "Mobile",
  Desktop = "Desktop",
}

const MOBILE_SPEED = 1.4;
const DESKTOP_SPEED = 1;
const BREAKPOINT = 768;
const useScrollProgress = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const animationId = useRef<number>(null);
  const initialTop = useRef(0);
  const { welcomeMovingFigure } = useClientContext();
  const translateYRef = useRef(0);

  const [viewportType, setViewportType] = useState(ViewportType.Mobile);

  const startYRef = useRef(0);
  const endYRef = useRef(0);
  const distanceRef = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      if (
        viewportType === ViewportType.Mobile &&
        window.innerWidth >= BREAKPOINT
      ) {
        setViewportType(ViewportType.Desktop);
        return;
      }

      if (
        viewportType === ViewportType.Desktop &&
        window.innerWidth < BREAKPOINT
      ) {
        setViewportType(ViewportType.Mobile);
        return;
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [viewportType]);

  const [scrollProgress, setScrollProgress] = useState(0);

  const getAbsoluteTop = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();

    return rect.top + window.scrollY;
  };

  useEffect(() => {
    const wrapper = containerRef.current;
    const targetSpace = welcomeMovingFigure.endRef.current;

    if (!wrapper || !targetSpace) {
      return;
    }

    const updatePositions = () => {
      // temporary remove styles
      wrapper.style.position = "static";
      targetSpace.style.transform = "none";
      // Get organic position
      const rect = wrapper.getBoundingClientRect();
      initialTop.current = rect.top + window.scrollY - rect.height / 2;
      // Styles to initial
      wrapper.style.position = "absolute";
      wrapper.style.transform = `translateY(${translateYRef.current}px)`;

      const startY =
        viewportType === ViewportType.Desktop ? 0 : getAbsoluteTop(wrapper);
      const endY = getAbsoluteTop(targetSpace);
      const distance =
        endY -
        startY -
        (viewportType === ViewportType.Desktop ? getAbsoluteTop(wrapper) : 0);

      startYRef.current = startY;
      endYRef.current = endY;
      distanceRef.current = distance;
    };

    // Apply the figure's translateY + Spline progress for a given scroll offset.
    const applyProgress = (scrollY: number) => {
      let progress = (scrollY - startYRef.current) / distanceRef.current;
      progress = Math.min(1, Math.max(0, progress));

      const speed =
        viewportType === ViewportType.Mobile ? MOBILE_SPEED : DESKTOP_SPEED;
      progress = Math.min(1, progress * speed);

      setScrollProgress(progress * 100);

      translateYRef.current = progress * distanceRef.current;
      if (containerRef.current) {
        containerRef.current.style.transform = `translateY(${translateYRef.current}px)`;
      }
    };

    updatePositions();
    welcomeMovingFigure.setStartRef(wrapper);

    // Driven by ScrollTrigger; self.scroll() returns the effective scroll
    // position of the scroller.
    const trigger = ScrollTrigger.create({
      start: 0,
      end: () => ScrollTrigger.maxScroll(window),
      invalidateOnRefresh: true,
      onRefresh: (self) => {
        updatePositions();
        applyProgress(self.scroll());
      },
      onUpdate: (self) => {
        animationId.current = requestAnimationFrame(() =>
          applyProgress(self.scroll())
        );
      },
    });

    applyProgress(trigger.scroll());

    return () => {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
      trigger.kill();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [welcomeMovingFigure.endRef, viewportType]);

  return { scrollProgress, viewportType };
};

export default function WelcomeMovingFigure() {
  const { mainFigureSplineRef } = useClientContext();

  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollProgress, viewportType } = useScrollProgress({
    containerRef,
  });

  // update transformation effect
  useEffect(() => {
    if (!mainFigureSplineRef) {
      return;
    }

    const currentPosition =
      viewportType === ViewportType.Mobile
        ? interpolate(BEGIN_MOBILE, END_MOBILE, scrollProgress)
        : interpolate(BEGIN_DESKTOP, END_DESKTOP, scrollProgress);

    // set animation state depending on scroll state
    for (const [key, value] of Object.entries(currentPosition)) {
      mainFigureSplineRef.setVariable(key, value);
    }
  }, [mainFigureSplineRef, scrollProgress, viewportType]);

  return (
    <div
      ref={containerRef}
      className={s.container}
    >
      <Scene
        desktopSrc="https://prod.spline.design/B38kUldCxyUKWpLR/scene.splinecode"
        mobileSrc="https://prod.spline.design/64Z0ZwO3H5vV0MQh/scene.splinecode"
      />
    </div>
  );
}
